import React, { useState, useRef, useCallback } from 'react';
import { DropdownChevronDownSvg } from 'apps/sigma2-ui/src/components/svg/DropdownChevronDown';
import cn from 'classnames';

/**
 * Configuration for a single option in the CustomSelect dropdown
 */
export interface CustomSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Props for the CustomSelect component
 */
export interface CustomSelectProps {
  selectedValue: string;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * A reusable custom select/dropdown component with icon support and consistent styling.
 *
 * Features:
 * - Support for icons in options
 * - Keyboard accessible
 * - Click outside to close
 * - Active/focused states with royal color scheme
 *
 * @example
 * ```tsx
 * const options = [
 *   { value: 'table', label: 'Table', icon: <TableIcon /> },
 *   { value: 'chart', label: 'Chart', icon: <ChartIcon /> }
 * ];
 *
 * <CustomSelect
 *   selectedValue={viewMode}
 *   options={options}
 *   onChange={setViewMode}
 *   placeholder="Select view..."
 * />
 * ```
 */
export const CustomSelect: React.FC<CustomSelectProps> = ({
  selectedValue,
  options,
  onChange,
  placeholder = 'Select...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(
    (option) => option.value === selectedValue,
  );

  const handleOptionClick = useCallback(
    (value: string) => {
      onChange(value);
      setIsOpen(false);
    },
    [onChange],
  );

  // Click outside handler
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative w-fit min-w-0" ref={containerRef}>
      <button
        type="button"
        className={`flex rounded py-2 px-3 gap-2 items-center transition-all cursor-pointer whitespace-nowrap border border-gray-200 ${
          isOpen
            ? 'bg-royal-100 border-royal-400 text-royal-700'
            : 'bg-white text-gray-900'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedOption?.icon && (
          <span className="flex-shrink-0">{selectedOption.icon}</span>
        )}
        <span className="text-xs">{selectedOption?.label || placeholder}</span>
        <DropdownChevronDownSvg
          svg={{ className: 'w-4 h-4 flex-shrink-0' }}
          wantsCurrentColor
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 z-50 w-max min-w-full mt-1 overflow-auto bg-white rounded-lg shadow-lg max-h-60 focus:outline-none">
          <ul role="listbox" className="py-1">
            {options.map((option) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === selectedValue}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-xs transition-colors whitespace-nowrap',
                  {
                    'bg-royal-100 text-royal-700':
                      option.value === selectedValue && !option.disabled,
                    'hover:bg-gray-100':
                      option.value !== selectedValue && !option.disabled,
                    'cursor-not-allowed text-gray-400': option.disabled,
                    'cursor-pointer': !option.disabled,
                  },
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (option.disabled) {
                    return;
                  }

                  handleOptionClick(option.value);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
              >
                {option.icon && (
                  <span className="flex-shrink-0">{option.icon}</span>
                )}
                <span className="font-small">{option.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
